import React, { useImperativeHandle, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Box,
    CircularProgress
} from '@mui/material';
import { useSettingsStore } from '@core/store';
import { useTranslation } from 'react-i18next';
import { WcnButton } from '@/components';

export interface ConfirmationDialogProps {
    // Content props
    heading?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;

    // Control props
    isSaving?: boolean;
    waitForConfirm?: boolean;

    // Event handlers
    onConfirm?: () => void | Promise<void>;
    onClose?: () => void;
    icon?: string | React.ReactNode;
}

export interface ConfirmationDialogRef {
    openDialog: () => void;
}

type Props = ConfirmationDialogProps & {
    dialogRef?: React.Ref<ConfirmationDialogRef>;
};

const ConfirmationDialogWithIcon = ({
    // Content props
    heading = '',
    message = '',
    confirmText = '',
    cancelText = '',

    // Control props
    isSaving = false,
    waitForConfirm = false,

    // Event handlers
    onConfirm,
    onClose,

    // Ref prop
    dialogRef,
    icon = ''
}: Props) => {
    const [open, setOpen] = useState(false);
    const { isMobile } = useSettingsStore();
    const { t } = useTranslation();

    useImperativeHandle(dialogRef, () => ({
        openDialog: () => setOpen(true)
    }));

    const handleConfirm = async () => {
        if (!waitForConfirm) {
            onConfirm?.();
            setOpen(false);
            return;
        }

        await onConfirm?.();
        setOpen(false);
    };

    const handleClose = () => {
        onClose?.();
        setOpen(false);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth={isMobile}
            fullScreen={false}
            className="dialog-container"
            PaperProps={{ sx: { borderRadius: 2, px: 2, py: 1 } }}
        >
            <DialogTitle
                sx={{
                    textAlign: 'center',
                    fontWeight: 400,
                    fontSize: '1rem',
                    textTransform: 'uppercase',
                    pt: 4
                }}
            >
                {heading}
            </DialogTitle>

            {icon && <Box className="dialog-icon">{icon}</Box>}

            <DialogContent>
                <DialogContentText
                    sx={{
                        textAlign: 'center',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-line',
                        fontSize: '14px'
                    }}
                >
                    {message}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'center' }}>
                {isMobile ? (
                    <DialogActions
                        sx={{
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            pb: 5
                        }}
                    >
                        <WcnButton onClick={handleClose} variant="red" style={{ width: 240 }}>
                            {cancelText ? t(cancelText) : t('no')}
                        </WcnButton>

                        <WcnButton
                            onClick={handleConfirm}
                            disabled={isSaving}
                            variant="primary"
                            style={{ width: 240, margin: 0 }}
                        >
                            {isSaving ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                                    <CircularProgress size={14} sx={{ color: '#ffffff' }} />
                                    <span>{t('saving')}</span>
                                </Box>
                            ) : confirmText ? (
                                t(confirmText)
                            ) : (
                                t('yes')
                            )}
                        </WcnButton>
                    </DialogActions>
                ) : (
                    <DialogActions
                        sx={{
                            padding: 3,
                            gap: 1,
                            justifyContent: 'center'
                        }}
                    >
                        <WcnButton onClick={handleClose} variant="red" style={{ flex: 1, width: 240 }}>
                            {cancelText ? t(cancelText) : t('no')}
                        </WcnButton>

                        <WcnButton
                            onClick={handleConfirm}
                            disabled={isSaving}
                            variant="primary"
                            style={{ flex: 1, width: 240 }}
                        >
                            {isSaving ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                                    <CircularProgress size={14} sx={{ color: '#ffffff' }} />
                                    <span>{t('saving')}</span>
                                </Box>
                            ) : confirmText ? (
                                t(confirmText)
                            ) : (
                                t('yes')
                            )}
                        </WcnButton>
                    </DialogActions>
                )}
            </DialogActions>
        </Dialog>
    );
};

ConfirmationDialogWithIcon.displayName = 'NewConfirmationDialog';

export default ConfirmationDialogWithIcon;
